import React, { useState, useEffect } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { userPreferencesService } from '../../../services/supabaseService';
import { useAuth } from '../../../contexts/AuthContext';

const NotificationSettings = ({ userRole, onSave }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    matchAlerts: true,
    messageAlerts: true,
    revealRequestAlerts: true,
    interviewReminders: true,
    interviewReminderTiming: '24hours',
    weeklyDigest: true,
    marketingEmails: false,
    frequency: 'instant'
  });

  useEffect(() => {
    const loadSettings = async () => {
      if (!user) return;

      try {
        const prefs = await userPreferencesService?.get(user?.id);
        if (prefs?.notificationSettings) {
          setSettings(prefs?.notificationSettings);
        }
      } catch (err) {
        console.error('Error loading notification settings:', err);
      }
    };

    loadSettings();
  }, [user]);

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleSelectChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      await userPreferencesService?.upsert(user?.id, {
        notificationSettings: settings
      });
      onSave?.('Notification settings saved successfully');
    } catch (err) {
      console.error('Error saving notification settings:', err);
    }
  };

  const frequencyOptions = [
    { value: 'instant', label: 'Instant' },
    { value: 'hourly', label: 'Hourly Digest' },
    { value: 'daily', label: 'Daily Digest' },
    { value: 'weekly', label: 'Weekly Digest' }
  ];

  const reminderOptions = [
    { value: '1hour', label: '1 Hour Before' },
    { value: '24hours', label: '24 Hours Before' },
    { value: '48hours', label: '48 Hours Before' },
    { value: 'week', label: '1 Week Before' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold text-[#111827] mb-2">Notification Settings</h2>
        <p className="text-sm text-[#6B7280]">
          Control how and when you receive notifications about your BlinD activity
        </p>
      </div>
      {/* Match Alerts */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Users" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Match Alerts</h3>
        </div>
        
        <Checkbox
          checked={settings?.matchAlerts}
          onChange={() => handleToggle('matchAlerts')}
          label="Enable match notifications"
          description="Get notified when you have new matches"
        />

        {settings?.matchAlerts && (
          <div className="ml-6 space-y-3">
            <Select
              label="Notification frequency"
              options={frequencyOptions}
              value={settings?.frequency}
              onChange={(value) => handleSelectChange('frequency', value)}
            />
          </div>
        )}
      </div>
      {/* Message Notifications */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="MessageSquare" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Messages</h3>
        </div>
        
        <Checkbox
          checked={settings?.messageAlerts}
          onChange={() => handleToggle('messageAlerts')}
          label="Enable message notifications"
          description="Get notified when you receive new messages"
        />

        {settings?.messageAlerts && (
          <div className="ml-6">
            <Checkbox
              checked={settings?.messageSound}
              onChange={() => handleToggle('messageSound')}
              label="Play sound for new messages"
            />
          </div>
        )}
      </div>
      {/* Interview Reminders */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Calendar" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Interview Reminders</h3>
        </div>
        
        <Checkbox
          checked={settings?.interviewReminders}
          onChange={() => handleToggle('interviewReminders')}
          label="Enable interview reminders"
          description="Get notified before scheduled interviews"
        />

        {settings?.interviewReminders && (
          <div className="ml-6 space-y-3">
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-2">
                Reminder Timing
              </label>
              <Select
                value={settings?.interviewReminderTiming}
                onChange={(e) => handleSelectChange('interviewReminderTiming', e?.target?.value)}
                options={reminderOptions}
              />
              <p className="text-xs text-[#6B7280] mt-1">
                Receive email reminders at your preferred time before interviews
              </p>
            </div>
          </div>
        )}
      </div>
      {/* Email Notification Toggle */}
      <div className="space-y-4 pt-6 border-t border-[#E5E7EB]">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Mail" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Email Notifications</h3>
        </div>
        
        <Checkbox
          checked={settings?.emailNotifications}
          onChange={() => handleToggle('emailNotifications')}
          label="Enable email notifications"
          description="Receive real-time email alerts for matches, messages, and interview reminders"
        />

        {settings?.emailNotifications && (
          <div className="ml-6 p-4 bg-[#F0E7FF] rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} color="#5B2DFF" className="mt-0.5" />
              <div className="text-sm text-[#374151]">
                <p className="font-medium mb-1">Email notifications are enabled for:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  {settings?.matchAlerts && <li>New matches</li>}
                  {settings?.messageAlerts && <li>New messages</li>}
                  {settings?.interviewReminders && <li>Interview reminders ({settings?.interviewReminderTiming?.replace('hours', ' hours')?.replace('hour', ' hour')?.replace('week', ' week')} before)</li>}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* System Updates */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Bell" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">System & Updates</h3>
        </div>
        
        <Checkbox
          checked={settings?.systemUpdates}
          onChange={() => handleToggle('systemUpdates')}
          label="System notifications"
          description="Important updates about the BlinD platform"
        />

        <Checkbox
          checked={settings?.weeklyDigest}
          onChange={() => handleToggle('weeklyDigest')}
          label="Weekly activity digest"
          description="Receive a summary of your weekly activity"
        />
      </div>
      {/* Delivery Channels */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[#E5E7EB]">
          <Icon name="Send" size={20} color="#5B2DFF" />
          <h3 className="text-lg font-semibold text-[#111827]">Delivery Channels</h3>
        </div>
        
        <Checkbox
          checked={settings?.pushNotifications}
          onChange={() => handleToggle('pushNotifications')}
          label="Push notifications"
          description="Receive browser push notifications"
        />

        <Checkbox
          checked={settings?.smsNotifications}
          onChange={() => handleToggle('smsNotifications')}
          label="SMS notifications"
          description="Receive critical notifications via SMS"
        />
      </div>
      {/* Save Button */}
      <div className="pt-6 border-t border-[#E5E7EB] flex justify-end">
        <Button
          onClick={handleSave}
          iconName="Save"
          className="bg-gradient-to-r from-[#5B2DFF] to-[#7C3FFF]"
        >
          Save Notification Settings
        </Button>
      </div>
    </div>
  );
};

export default NotificationSettings;